import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

function Question({ question }: { question: Question }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={cn(
        "bg-[#F8F9FA] w-full border border-[#0D2A4C08] rounded-[100px] py-[22px] px-7",
        open && "rounded-[12px]"
      )}
    >
      <Collapsible
        open={open}
        onOpenChange={(isOpen) => setOpen(isOpen)}
        className="flex flex-col w-full"
      >
        <CollapsibleTrigger className="w-full flex justify-between items-center cursor-pointer">
          <p className="text-[#0D2A4C] tracking-tight lg:text-[16px] lg:leading-4 font-semibold text-start">
            {question.question}
          </p>
          {open && <ChevronUpIcon className="lg:size-[22px]" />}
          {!open && <ChevronDownIcon className="lg:size-[22px]" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3.5 lg:pt-4">
          <p className="text-[#0D2A4C99] font-medium lg:text-[16px] lg:leading-[22px] tracking-tight">
            {question.answer}
          </p>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

interface Question {
  question: string;
  answer: string;
}

const questions: Array<Question> = [
  {
    question: "What pricing plans does ListKit offer?",
    answer:
      "ListKit offers flexible subscription plans, including tiered options based on your lead generation needs and sales tools usage. You can choose the plan that best fits your business size and goals.",
  },
  {
    question: "Is there a free trial available?",
    answer:
      "Yes, ListKit provides a free trial to help you explore the platform and test its features before committing to a paid plan.",
  },
  {
    question: "How can I cancel my subscription?",
    answer:
      "You can easily cancel your subscription anytime by accessing your account settings or by contacting customer support.",
  },
  {
    question: "Are there any additional fees for extra features?",
    answer:
      "Additional features may be available at different pricing tiers. Custom add-ons or premium services could incur extra fees.",
  },
  {
    question: "Does ListKit offer discounts for long-term plans?",
    answer:
      "Yes, ListKit offers discounts for annual subscriptions compared to monthly plans.",
  },
  {
    question: "Can I switch between pricing plans?",
    answer:
      "Yes, you can upgrade or downgrade your plan at any time based on your evolving needs.",
  },
];

export default function FaqSection() {
  return (
    <section id="faq-section">
      <div className="flex justify-center w-full p-10 px-[18px] lg:px-18">
        <div className="flex flex-col items-center justify-center w-full max-w-[1200px] gap-14">
          <div className="flex flex-col lg:flex-row justify-between gap-[38px] xl:gap-20 items-center lg:items-start w-full">
            <div className="flex flex-col gap-5 lg:gap-3 items-center lg:items-start">
              <h1 className="md:max-w-[450px] max-w-[253px] text-center lg:text-start text-wrap !font-semibold text-[#0D2A4C] !text-[32px] md:!text-[56px] md:leading-[58px] leading-[32px] tracking-tighter">
                Have Questions? We Have Answers
              </h1>
              <p className="text-[#0D2A4C99] !font-medium text-sm lg:text-[16px] max-w-[250px] lg:max-w-[347px] tracking-tight text-wrap text-center lg:text-start">
                Still stuck? Visit our{" "}
                <Link
                  href="https://docs.listkit.io/en/"
                  className="text-[#2A8DFF] cursor-pointer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Help Center
                </Link>{" "}
                to message our team. Average response time is under 2 hours.
              </p>
            </div>
            <div className="flex flex-col gap-3 lg:gap-3.5 flex-1 w-full ">
              {questions?.map((question, index) => (
                <Question
                  key={`${question.question}-${index}`}
                  question={question}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
